// ../../react-shim.js
import React from "react";

// src/checkbox.tsx
import {
  clsx,
  css,
  forwardRef,
  nature
} from "@nature-ui/system";

// ../utils/dist/index.esm.js
import React2 from "react";
import React22 from "react";
var addItem = (array, item) => {
  return [...array, item];
};
var removeItem = (array, item) => {
  return array.filter((eachItem) => eachItem !== item);
};
var isFunction = (value) => {
  return typeof value === "function";
};
var isArray = (value) => {
  return Array.isArray(value);
};
var isObject = (value) => {
  const type = typeof value;
  return value !== null && (type === "object" || type === "function") && !isArray(value);
};
var isInputEvent = (value) => value && isObject(value) && isObject(value.target);
var __DEV__ = process.env.NODE_ENV !== "production";
var _window;
try {
  _window = window;
} catch {
}
var getWindow = (node) => {
  var _a;
  return ((_a = node == null ? void 0 : node.ownerDocument) == null ? void 0 : _a.defaultView) ?? _window;
};
var checkIsBrowser = () => {
  const __window = getWindow();
  return Boolean(
    typeof __window !== "undefined" && __window.document && __window.document.createElement
  );
};
var isBrowser = checkIsBrowser();
var dataAttr = (condition) => condition ? "" : void 0;
var SELECTORS = [
  "input",
  "select",
  "textarea",
  "a[href]",
  "area[href]",
  "button",
  "[tabindex]",
  "audio[controls]",
  "video[controls]",
  "[contenteditable]:not([contenteditable=false])"
];
var selector = SELECTORS.join();
var callAllHandler = (...fns) => {
  return (event) => {
    fns.some((fn) => {
      fn == null ? void 0 : fn(event);
      return event == null ? void 0 : event.defaultPrevented;
    });
  };
};
var minSafeInteger = Number.MIN_SAFE_INTEGER || -9007199254740991;
var maxSafeInteger = Number.MAX_SAFE_INTEGER || 9007199254740991;
var omit = (object, keys) => {
  const result = {};
  Object.keys(object).forEach((key) => {
    if (!keys.includes(key)) {
      result[key] = object[key];
    }
  });
  return result;
};
var createContext = (options = {}) => {
  const {
    strict = true,
    errorMessage = "useContext must be inside a Provider with a value",
    name
  } = options;
  const Context = React22.createContext(void 0);
  Context.displayName = name;
  const useContext = () => {
    const context = React22.useContext(Context);
    if (!context && strict)
      throw new Error(errorMessage);
    return context;
  };
  return [
    Context.Provider,
    useContext,
    Context
  ];
};
var assignRef = (ref, value) => {
  if (ref === null || ref === void 0 || !ref)
    return;
  if (isFunction(ref)) {
    ref(value);
    return;
  }
  try {
    ref.current = value;
  } catch (error) {
    throw new Error(`Cannot assign value '${value}' to ref '${ref}'`);
  }
};
var mergeRefs = (...refs) => (value) => {
  refs.forEach((ref) => assignRef(ref, value));
};

// src/checkbox-group.tsx
import React4 from "react";

// src/use-checkbox-group.ts
import { useControllableProp } from "@nature-ui/hooks";
import React3 from "react";
var useCheckboxGroup = (props) => {
  const {
    defaultValue,
    value: valueProp,
    onChange: onChangeProp,
    isNative,
    ...rest
  } = props;
  const [valueState, setValue] = React3.useState(defaultValue || []);
  const [isControlled, value] = useControllableProp(valueProp, valueState);
  const updateValue = React3.useCallback(
    (nextState) => {
      if (!isControlled) {
        setValue(nextState);
      }
      onChangeProp == null ? void 0 : onChangeProp(nextState);
    },
    [isControlled, onChangeProp]
  );
  const onChange = React3.useCallback(
    (eventOrValue) => {
      if (!value)
        return;
      const isChecked = isInputEvent(eventOrValue) ? eventOrValue.target.checked : !value.includes(eventOrValue);
      const selectedValue = isInputEvent(eventOrValue) ? eventOrValue.target.value : eventOrValue;
      const nextValue = isChecked ? addItem(value, selectedValue) : removeItem(value, selectedValue);
      updateValue(nextValue);
    },
    [updateValue, value]
  );
  return {
    ...rest,
    value,
    onChange,
    setValue: updateValue,
    getCheckboxProps: (prop) => {
      const checkedKey = isNative ? "checked" : "isChecked";
      return {
        ...prop,
        [checkedKey]: value.includes(prop.value),
        onChange
      };
    }
  };
};

// src/checkbox-group.tsx
import { jsx } from "react/jsx-runtime";
var [CheckboxGroupContextProvider, useCheckboxGroupContext] = createContext({
  name: "CheckboxGroupContext",
  strict: false
});
var CheckboxGroup = (props) => {
  const { children, color, hoverColor, size } = props;
  const { value, onChange } = useCheckboxGroup(props);
  const group = React4.useMemo(
    () => ({
      onChange,
      value,
      color,
      hoverColor,
      size
    }),
    [onChange, value]
  );
  return /* @__PURE__ */ jsx(CheckboxGroupContextProvider, {
    value: group,
    children
  });
};
if (__DEV__) {
  CheckboxGroup.displayName = "CheckboxGroup";
}

// src/checkbox-icon.tsx
import { Icon } from "@nature-ui/icon";
import { jsx as jsx2 } from "react/jsx-runtime";
var CheckIcon = (props) => {
  return /* @__PURE__ */ jsx2(Icon, {
    viewBox: "0 0 14 14",
    ...props,
    children: /* @__PURE__ */ jsx2("g", {
      fill: "currentColor",
      children: /* @__PURE__ */ jsx2("polygon", {
        points: "5.5 11.9993304 14 3.49933039 12.5 2 5.5 8.99933039 1.5 4.9968652 0 6.49933039"
      })
    })
  });
};
var MinusIcon = (props) => {
  return /* @__PURE__ */ jsx2(Icon, {
    viewBox: "0 0 24 24",
    ...props,
    children: /* @__PURE__ */ jsx2("rect", {
      fill: "currentColor",
      height: "4",
      width: "20",
      x: "2",
      y: "10"
    })
  });
};
var CheckboxIcon = (props) => {
  const { isChecked, isIndeterminate, ...rest } = props;
  if (isChecked) {
    return /* @__PURE__ */ jsx2(CheckIcon, {
      ...rest
    });
  }
  if (isIndeterminate) {
    return /* @__PURE__ */ jsx2(MinusIcon, {
      ...rest
    });
  }
  return null;
};

// src/use-checkbox.ts
import { useFormControlProps } from "@nature-ui/form-control";
import {
  useBoolean,
  useCallbackRef,
  useControllableProp as useControllableProp2,
  useSafeLayoutEffect
} from "@nature-ui/hooks";
import { visuallyHiddenStyle } from "@nature-ui/visually-hidden";
import { trackFocusVisible } from "@zag-js/focus-visible";
import React5, { useCallback, useEffect } from "react";
var stopEvent = (event) => {
  event.preventDefault();
  event.stopPropagation();
};
var useCheckbox = (props = {}) => {
  const formControlProps = useFormControlProps(props);
  const {
    isDisabled,
    isReadOnly,
    isRequired,
    isInvalid,
    id,
    onBlur,
    onFocus,
    "aria-describedby": ariaDescribedBy
  } = formControlProps;
  const {
    defaultIsChecked,
    isChecked: checkedProp,
    isFocusable,
    onChange,
    isIndeterminate,
    name,
    value,
    tabIndex = void 0,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-invalid": ariaInvalid,
    ...rest
  } = props;
  const htmlProps = omit(rest, [
    "isDisabled",
    "isReadOnly",
    "isRequired",
    "isInvalid",
    "id",
    "aria-describedby"
  ]);
  const [isFocused, setFocused] = useBoolean();
  const [isHovered, setHovered] = useBoolean();
  const [isActive, setActive] = useBoolean();
  const [rootIsLabelElement, setRootIsLabelElement] = useBoolean(true);
  const [isFocusVisible, setIsFocusVisible] = useBoolean();
  const ref = React5.useRef(null);
  const [checkedState, setCheckedState] = useBoolean(Boolean(defaultIsChecked));
  useEffect(() => {
    return trackFocusVisible(setIsFocusVisible.on);
  }, []);
  const [isControlled, isChecked] = useControllableProp2(
    checkedProp,
    checkedState
  );
  const onChangeProp = useCallbackRef(onChange);
  const onBlurProp = useCallbackRef(onBlur);
  const onFocusProp = useCallbackRef(onFocus);
  const handleChange = useCallback(
    (event) => {
      if (isReadOnly || isDisabled) {
        event.preventDefault();
        return;
      }
      if (!isControlled) {
        if (isChecked) {
          setCheckedState.setState(event.target.checked);
        } else {
          setCheckedState.setState(
            isIndeterminate ? true : event.target.checked
          );
        }
      }
      onChangeProp == null ? void 0 : onChangeProp(event);
    },
    [
      isReadOnly,
      isDisabled,
      isChecked,
      isControlled,
      isIndeterminate,
      onChangeProp
    ]
  );
  useSafeLayoutEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = Boolean(isIndeterminate);
    }
  }, [isIndeterminate]);
  const trulyDisabled = isDisabled && !isFocusable;
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === " ") {
        setActive.on();
      }
    },
    [setActive]
  );
  const handleKeyUp = useCallback(
    (event) => {
      if (event.key === " ") {
        setActive.off();
      }
    },
    [setActive]
  );
  const getRootProps = useCallback(
    (props2 = {}, forwardedRef = null) => ({
      ...htmlProps,
      ...props2,
      ref: mergeRefs(forwardedRef, (node) => {
        if (!node)
          return;
        setRootIsLabelElement.setState(node.tagName === "LABEL");
      }),
      onClick: callAllHandler(props2.onClick, () => {
        var _a;
        if (!rootIsLabelElement) {
          (_a = ref.current) == null ? void 0 : _a.click();
          requestAnimationFrame(() => {
            var _a2;
            (_a2 = ref.current) == null ? void 0 : _a2.focus();
          });
        }
      }),
      "data-disabled": dataAttr(isDisabled),
      "data-checked": dataAttr(isChecked),
      "data-invalid": dataAttr(isInvalid)
    }),
    [htmlProps, isDisabled, isChecked, isInvalid, rootIsLabelElement]
  );
  const getCheckboxProps = useCallback(
    (prop = {}, forwardedRef = null) => {
      const onPressDown = (event) => {
        if (isFocused) {
          event.preventDefault();
        }
        setActive.on();
      };
      return {
        ...prop,
        ref: forwardedRef,
        "data-active": dataAttr(isActive),
        "data-hover": dataAttr(isHovered),
        "data-checked": dataAttr(isChecked),
        "data-focus": dataAttr(isFocused),
        "data-indeterminate": dataAttr(isIndeterminate),
        "data-disabled": dataAttr(isDisabled),
        "data-invalid": dataAttr(isInvalid),
        "data-readonly": dataAttr(isReadOnly),
        "aria-hidden": true,
        onMouseDown: callAllHandler(prop.onMouseDown, onPressDown),
        onMouseUp: callAllHandler(prop.onMouseUp, setActive.off),
        onMouseEnter: callAllHandler(prop.onMouseEnter, setHovered.on),
        onMouseLeave: callAllHandler(prop.onMouseLeave, setHovered.off),
        style: {
          touchAction: "none",
          ...prop.style
        }
      };
    },
    [
      isActive,
      isChecked,
      isDisabled,
      isFocused,
      isFocusVisible,
      isHovered,
      isIndeterminate,
      isInvalid,
      isReadOnly
    ]
  );
  const getInputProps = useCallback(
    (prop = {}, forwardedRef = null) => {
      return {
        ...prop,
        ref: mergeRefs(ref, forwardedRef),
        type: "checkbox",
        name,
        value,
        id,
        onChange: callAllHandler(prop.onChange, handleChange),
        onBlur: callAllHandler(prop.onBlur, onBlurProp, setFocused.off),
        onFocus: callAllHandler(prop.onFocus, onFocusProp, setFocused.on),
        onKeyDown: callAllHandler(prop.onKeyDown, handleKeyDown),
        onKeyUp: callAllHandler(prop.onKeyUp, handleKeyUp),
        required: isRequired,
        checked: isChecked,
        disabled: trulyDisabled,
        readOnly: isReadOnly,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        "aria-invalid": ariaInvalid ? Boolean(ariaInvalid) : isInvalid,
        "aria-describedby": ariaDescribedBy,
        "aria-disabled": isDisabled,
        style: visuallyHiddenStyle
      };
    },
    [
      name,
      value,
      id,
      handleChange,
      onBlurProp,
      onFocusProp,
      handleKeyDown,
      handleKeyUp,
      isRequired,
      isChecked,
      trulyDisabled,
      isReadOnly,
      ariaLabel,
      ariaLabelledBy,
      ariaInvalid,
      isInvalid,
      ariaDescribedBy,
      isDisabled,
      tabIndex
    ]
  );
  const getLabelProps = useCallback(
    (prop = {}, forwardedRef = null) => {
      return {
        ...prop,
        ref: forwardedRef,
        onMouseDown: callAllHandler(prop.onMouseDown, stopEvent),
        onTouchStart: callAllHandler(prop.onTouchState, stopEvent),
        "data-disabled": dataAttr(isDisabled),
        "data-checked": dataAttr(isChecked),
        "data-invalid": dataAttr(isInvalid)
      };
    },
    [isChecked, isDisabled, isInvalid]
  );
  const state = {
    isInvalid,
    isFocused,
    isChecked,
    isActive,
    isHovered,
    isIndeterminate,
    isDisabled,
    isReadOnly,
    isRequired
  };
  return {
    state,
    getRootProps,
    getCheckboxProps,
    getInputProps,
    getLabelProps,
    htmlProps
  };
};

// src/checkbox.tsx
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
function getBgAndHover(defaultClassName, className) {
  var _a, _b;
  let _className = `${className} ${defaultClassName}`;
  const hoverRegex = /hover:bg-\w+(-\d+)?/i;
  const bgRegex = /bg-\w+(-\d+)?/i;
  let baseHoverColor = ((_a = _className.match(hoverRegex)) == null ? void 0 : _a[0]) ?? "";
  _className = _className == null ? void 0 : _className.replace(hoverRegex, "");
  let baseBg = ((_b = _className.match(bgRegex)) == null ? void 0 : _b[0]) ?? "";
  return [baseHoverColor, baseBg];
}
var StyledControl = forwardRef((props, ref) => {
  const { color, ...rest } = props;
  const _checked = typeof props["data-checked"] !== "undefined";
  const _focus = typeof props["data-focus"] !== "undefined";
  const _indeterminate = typeof props["data-indeterminate"] !== "undefined";
  const _disabled = typeof props["data-disabled"] !== "undefined";
  const _invalid = typeof props["data-invalid"] !== "undefined";
  const _hover = typeof props["data-hover"] !== "undefined";
  const DEFAULTS = "box-border inline-flex items-center justify-center align-top select-none flex-shrink-0 text-white rounded w-4 h-4 p-0 transition-all duration-300 ease-in-out border-2";
  const defaultColor = "bg-blue-500 hover:bg-blue-700";
  const [hoverColor, bg] = getBgAndHover(defaultColor, color);
  const _className = clsx(DEFAULTS, {
    [`border-none`]: !_invalid && _checked || _indeterminate,
    ["ring"]: _focus,
    [bg]: _checked && !_disabled || _indeterminate,
    [hoverColor]: _hover && _checked && !_disabled,
    ["bg-gray-300"]: _disabled,
    ["border-red-500"]: _invalid
  });
  return /* @__PURE__ */ jsx3(nature.div, {
    className: _className,
    ref,
    ...rest
  });
});
var StyledLabel = forwardRef(({ spacing, ...props }, ref) => {
  const _disabled = typeof props["data-disabled"] !== "undefined";
  const styles = css`
    margin-left: ${spacing};
  `;
  const _className = clsx(`select-none ${styles}`, {
    "text-gray-400": _disabled
  });
  return /* @__PURE__ */ jsx3(nature.span, {
    className: _className,
    ref,
    ...props
  });
});
var StyledWrapper = forwardRef(
  ({ className = "", ...props }, ref) => {
    const _className = clsx(
      "cursor-pointer inline-flex items-center align-top relative",
      {
        [className]: className
      }
    );
    return /* @__PURE__ */ jsx3(nature.label, {
      className: _className,
      ref,
      ...props
    });
  }
);
var Checkbox = forwardRef((props, ref) => {
  const group = useCheckboxGroupContext();
  const {
    className,
    spacing = "0.5rem",
    iconColor,
    children,
    color,
    size,
    ...rest
  } = props;
  const SPACING = typeof spacing === "string" ? spacing : `${spacing}px`;
  let { isChecked } = props;
  if ((group == null ? void 0 : group.value) && props.value) {
    isChecked = group.value.includes(props.value);
  }
  let { onChange } = props;
  if ((group == null ? void 0 : group.onChange) && props.value) {
    onChange = group.onChange;
  }
  const { state, getInputProps, getCheckboxProps, getLabelProps, htmlProps } = useCheckbox({
    ...rest,
    isChecked,
    onChange
  });
  return /* @__PURE__ */ jsxs(StyledWrapper, {
    className,
    ...htmlProps,
    children: [
      /* @__PURE__ */ jsx3("input", {
        ...getInputProps({ ref })
      }),
      /* @__PURE__ */ jsx3(StyledControl, {
        color,
        ...getCheckboxProps(),
        children: /* @__PURE__ */ jsx3(CheckboxIcon, {
          isChecked: state.isChecked,
          isIndeterminate: state.isIndeterminate,
          size,
          className: `inline-block flex-shrink-0 leading-4 align-middle transition-all duration-300 ease-in-out text-${iconColor || "current"}`
        })
      }),
      children && /* @__PURE__ */ jsx3(StyledLabel, {
        spacing: SPACING,
        ...getLabelProps(),
        children
      })
    ]
  });
});
if (__DEV__) {
  Checkbox.displayName = "Checkbox";
}
export {
  Checkbox,
  CheckboxGroup,
  useCheckbox,
  useCheckboxGroup,
  useCheckboxGroupContext
};
